import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
  addDays,
  addQuarters,
  format,
  parseISO,
  startOfQuarter,
  startOfWeek,
} from "date-fns";
import { apiForLine, type LamShiftRollsRow } from "../../../api/lamLine";
import { LoadingOverlay } from "../../common/LoadingOverlay";
import { bucketShiftRolls, type RollBucket } from "../../../utils/rollBuckets";
import {
  quarterLabel,
  quarterWeeks,
  type ScorecardWeek,
} from "../../../utils/scorecardWeeks";

/**
 * Weekly roll output for a quarter, one column per week. Opens on the current
 * quarter; earlier ones are a pick away, for reading back against a closed
 * scorecard.
 *
 * The plant already keeps this as a spreadsheet scorecard: measurables down the
 * side, weeks across the top. This is the same table off the PLC's own counters,
 * laid out to be read beside that sheet week for week.
 *
 * Each figure is broken out per line and then totalled, rather than only
 * totalled. A single combined row would hide the fact that Lamination 1 stops
 * reporting in early August while its PLC clock is uncommissioned — the total
 * would step down mid-quarter and read as a collapse in production rather than
 * a dead feed. Split out, the gap is visible as Lam 1's own row going to dashes.
 *
 * No goal column and no pass/fail colouring: the sheet carries the goals and
 * its own colouring, and a second opinion here would drift from it the first
 * time a goal changed. Numbers only; the sheet judges them.
 */

type Measurable = {
  key: string;
  label: string;
  /**
   * The week's figure, or null when the shift did not run that week.
   * `potential` is the rolls the PLC's own shift lengths could have produced.
   */
  value: (bucket: RollBucket, potential: number) => number | null;
  format: (value: number) => string;
  /**
   * Score only shifts that have finished. A ratio taken twenty minutes into a
   * shift says the crew is at 2%, which is not a fact about the crew.
   */
  completeShiftsOnly?: boolean;
};

/**
 * The pace potential output is measured against: the best Lamination 2 has
 * actually sustained for a full hour, a median 48.4 s per roll (2026-09-02).
 * Full shifts land between 49.2 and 50.1 s, so it is a real stretch and a
 * proven one rather than an aspiration.
 *
 * One bar for every shift, so crews are compared on equal terms rather than
 * each against its own pace — a shift running slowly would otherwise be scored
 * against a lower bar and its slow running would never show up as a loss.
 *
 * Frozen rather than recomputed from history on the fly: a moving bar means one
 * crew setting a record quietly lowers everybody else's score, and bonuses ride
 * on this number. Review it deliberately.
 */
const IDEAL_CYCLE_S = 48.4;
/**
 * The longest a shift is taken to be. The PLC's shift boundaries are the source
 * of truth for how long a shift ran — the gap to the next shift it recorded —
 * and this only caps that, so a stretch of days when the line stood idle cannot
 * be charged to the last crew that worked.
 */
const MAX_SHIFT_S = 12 * 60 * 60;

const MEASURABLES: Measurable[] = [
  {
    key: "oee",
    label: "OEE %",
    // Good rolls against what the shift could have made at the reference pace
    // for its whole scheduled twelve hours. Downtime is in the denominator by
    // construction: an hour not producing is an hour of potential lost.
    value: (b, potential) => (potential > 0 ? (b.a / potential) * 100 : null),
    format: (v) => `${v.toFixed(1)}%`,
    completeShiftsOnly: true,
  },
  {
    key: "a_rolls",
    label: "A-Rolls Produced",
    value: (b) => b.a,
    format: (v) => v.toLocaleString(),
  },
  {
    key: "b_rolls",
    label: "B-Rolls Produced",
    value: (b) => b.b,
    format: (v) => v.toLocaleString(),
  },
  {
    key: "b_pct",
    // The count above and the share here answer different questions: how many
    // came off B, and how much of the week's output that was. A week that ran
    // half as long can post a smaller count and a worse share at once.
    label: "B-Rolls % of Output",
    // Share of everything that came off the line, F included — the same
    // denominator the overview's quality mix uses, so the two agree. Per shift
    // it is that shift's own share, so the total is not a mean of the four.
    value: (b) => (b.total > 0 ? (b.b / b.total) * 100 : null),
    format: (v) => `${v.toFixed(2)}%`,
  },
];

/**
 * Which crew's figures a row carries: line and shift, which is the grain
 * performance is actually managed at. Total is the plant, not an average.
 */
type SeriesKey = "D1" | "D2" | "N1" | "N2" | "total";

const SERIES: Array<{ key: Exclude<SeriesKey, "total">; label: string }> = [
  { key: "D1", label: "D1" },
  { key: "D2", label: "D2" },
  { key: "N1", label: "N1" },
  { key: "N2", label: "N2" },
];

/**
 * Four separate hues rather than two shades of two: shades of a colour are
 * told apart by comparing them, and on a chart with four lines crossing each
 * other there is often nothing adjacent to compare against.
 *
 * Checked as a set rather than picked by eye — every pair of the four clears
 * both the colour-blind separation floor and the normal-vision floor, so no
 * two lines collapse into each other for any reader. Aqua sits a little under
 * the 3:1 contrast target against white, which the legend and the table of the
 * same figures above it cover.
 */
const SHIFT_COLOR: Record<Exclude<SeriesKey, "total">, string> = {
  D1: "#2a78d6",
  D2: "#eb6834",
  N1: "#1baf7a",
  N2: "#4a3aa7",
};

/**
 * The day shift starts at 06:00 and the night at 18:00, matching
 * settings_shifts. A handful of rows from July carry other start hours; they
 * fall to the night side rather than being dropped.
 */
const isDayShift = (row: LamShiftRollsRow) =>
  row.shift_hour >= 6 && row.shift_hour < 18;

/** Width of the frozen label column, so it can be offset in the scroll. */
const LABEL_W = 190;
/**
 * The narrowest a week column goes before the grid starts scrolling. Above that
 * the columns share the width equally — what matters for lining the chart up
 * with the table is that they are equal and that the chart reserves exactly
 * LABEL_W for its y axis, not that they are any particular size. Sized to
 * content they would be unequal, and the two would drift apart.
 */
const WEEK_W = 64;

const weekCell = { whiteSpace: "nowrap" } as const;

/**
 * The earliest quarter offered. Lamination 2's roll counts start 2026-07-26 and
 * Lamination 1's later still, so nothing before Q3 2026 has data to show.
 */
const FIRST_QUARTER = new Date(2026, 6, 1);

/** Every quarter with possible data, newest first, for the picker. */
const quarterOptions = (current: Date): Date[] => {
  const out: Date[] = [];
  for (let q = current; q >= FIRST_QUARTER; q = addQuarters(q, -1)) out.push(q);
  return out;
};

const quarterName = (q: Date) => `${quarterLabel(q)} ${q.getFullYear()}`;

/** Keeps the row labels readable while a quarter of weeks scrolls past. */
const stickyCell = {
  position: "sticky" as const,
  left: 0,
  zIndex: 2,
  minWidth: LABEL_W,
  width: LABEL_W,
  bgcolor: "background.paper",
};

/** RFC-4180 quoting: only what needs it, so the file stays readable. */
const csvField = (value: string) =>
  /[",\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;

/**
 * The table as a spreadsheet, off the same MEASURABLES and SERIES the table
 * renders from — so the file cannot drift from what is on screen.
 *
 * Figures go out unformatted: a count with a thousands separator is text to a
 * spreadsheet, not a number, and the whole point of the export is to compute
 * with it. A week with no shifts is an empty cell rather than a dash, so it
 * stays blank instead of importing as text; blank and zero are different
 * answers and this keeps them apart.
 */
export const buildCsv = (
  weeks: ScorecardWeek[],
  all: Record<SeriesKey, SeriesData>,
  complete: Record<SeriesKey, SeriesData>,
): string => {
  const lines: string[][] = [
    ["Qtr", ...weeks.map((w) => quarterLabel(w.start))],
    ["Week Num", ...weeks.map((w) => String(w.weekNum))],
    ["Date", ...weeks.map((w) => w.label)],
  ];

  for (const m of MEASURABLES) {
    const buckets = m.completeShiftsOnly ? complete : all;
    lines.push([m.label]);
    for (const s of [
      ...SERIES,
      { key: "total" as SeriesKey, label: "Total" },
    ]) {
      lines.push([
        s.label,
        ...weeks.map((w) => {
          const data = buckets[s.key];
          const bucket = data.buckets.get(w.start.getTime());
          const value = bucket
            ? m.value(bucket, data.potential.get(w.start.getTime()) ?? 0)
            : null;
          if (value === null) return "";
          // Percentages keep their sign; counts go out bare so a spreadsheet
          // reads them as numbers rather than text.
          return m.format(value).replace(/,/g, "");
        }),
      ]);
    }
  }

  return lines.map((cells) => cells.map(csvField).join(",")).join("\n");
};

/** Hands the browser a file. Nothing here is async — the data is already local. */
const downloadCsv = (csv: string, filename: string) => {
  const url = URL.createObjectURL(
    new Blob([csv], { type: "text/csv;charset=utf-8;" }),
  );
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  // Firefox only follows the click for a link that is in the document.
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/** Weekly buckets keyed by week start, so a column is a map lookup. */
const byWeek = (rows: LamShiftRollsRow[]) => {
  const map = new Map<number, RollBucket>();
  for (const bucket of bucketShiftRolls(rows, "week"))
    map.set(bucket.key, bucket);
  return map;
};

const startedAt = (row: LamShiftRollsRow) =>
  parseISO(row.shift_start).getTime();

/**
 * How long each shift ran, from the PLC's own boundaries.
 *
 * A shift lasts until the next shift the PLC recorded — that, not a nominal
 * twelve hours and not settings_shifts, is what actually happened on the line.
 * The gap is capped so that days the line stood idle are not charged to the
 * shift before them, and the newest shift, having no successor yet, takes the
 * cap. Lengths are worked out across the whole line's shifts before they are
 * split into day and night, or every gap would read as a day rather than a
 * handover.
 */
type TimedShift = { row: LamShiftRollsRow; seconds: number };

const withLengths = (lineRows: LamShiftRollsRow[]): TimedShift[] => {
  const sorted = [...lineRows].sort((a, b) => startedAt(a) - startedAt(b));
  return sorted.map((row, i) => {
    const next = sorted[i + 1];
    const gap = next ? (startedAt(next) - startedAt(row)) / 1000 : Infinity;
    return { row, seconds: Math.min(gap, MAX_SHIFT_S) };
  });
};

/**
 * Rolls those shifts could have produced at the reference pace, per week.
 *
 * The length travels with the row rather than being looked up by shift_id: the
 * PLC builds that id from the date and hour, so the two lines produce the same
 * id for the same shift and a single lookup table would have them overwrite
 * each other.
 */
const potentialByWeek = (shifts: TimedShift[]): Map<number, number> => {
  const out = new Map<number, number>();
  for (const { row, seconds } of shifts) {
    const at = startOfWeek(parseISO(row.shift_start));
    if (Number.isNaN(at.getTime())) continue;
    out.set(
      at.getTime(),
      (out.get(at.getTime()) ?? 0) + seconds / IDEAL_CYCLE_S,
    );
  }
  return out;
};

type SeriesData = {
  buckets: Map<number, RollBucket>;
  potential: Map<number, number>;
};

const WeeklyRollsReport: React.FC = () => {
  const [lam1Rows, setLam1Rows] = useState<LamShiftRollsRow[]>([]);
  const [lam2Rows, setLam2Rows] = useState<LamShiftRollsRow[]>([]);
  const [loading, setLoading] = useState(true);
  /** Which sections are open. Closed by default — see the section row below. */
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const toggle = (key: string) =>
    setOpen((prev) => ({ ...prev, [key]: !prev[key] }));
  const [failed, setFailed] = useState(false);

  // The current quarter is fixed at mount; the one on screen is any from
  // FIRST_QUARTER up to it, keyed by its first day.
  const current = useMemo(() => startOfQuarter(new Date()), []);
  const options = useMemo(() => quarterOptions(current), [current]);
  const [quarter, setQuarter] = useState<Date>(current);
  const weeks = useMemo(() => quarterWeeks(quarter), [quarter]);
  const oldest = options[options.length - 1];

  useEffect(() => {
    const ctrl = new AbortController();
    const from = quarter;
    // Another quarter's figures must not sit under this one's headings while
    // its own load.
    setLam1Rows([]);
    setLam2Rows([]);
    setLoading(true);
    // The window is the weeks on screen, not the calendar quarter: the last
    // column runs a few days past quarter end, and its shifts belong to it.
    const last = weeks[weeks.length - 1]?.start ?? from;

    const window = {
      // shift_start is a Nashville-local naive wall clock; send the same frame.
      start: format(startOfWeek(from), "yyyy-MM-dd'T'HH:mm:ss"),
      end: format(addDays(last, 7), "yyyy-MM-dd'T'HH:mm:ss"),
      signal: ctrl.signal,
    };

    Promise.all([
      apiForLine("lam1").getShiftRolls(window),
      apiForLine("lam2").getShiftRolls(window),
    ])
      .then(([lam1, lam2]) => {
        setLam1Rows(lam1);
        setLam2Rows(lam2);
        setFailed(false);
      })
      .catch(() => {
        // Switching quarter aborts the last fetch; that is not a failure.
        if (!ctrl.signal.aborted) setFailed(true);
      })
      .finally(() => {
        if (!ctrl.signal.aborted) setLoading(false);
      });

    return () => ctrl.abort();
  }, [quarter, weeks]);

  // Each line bucketed on its own, and the two bucketed together. The total is
  // built from the raw shifts rather than by adding the two lines' figures, so
  // a percentage total is the plant's share and not a mean of two shares.
  /**
   * Each crew bucketed on its own, and everything bucketed together. The total
   * is built from the raw shifts rather than by adding the four crews' figures,
   * so a percentage total is the plant's own and not a mean of four shares.
   *
   * Two sets: every shift, for the counts, and finished shifts only, for the
   * ratios that a shift still running would distort.
   */
  const { all, complete } = useMemo(() => {
    // Lengths come from each line's own shift sequence, so a handover reads as
    // a handover rather than as a gap.
    const timed1 = withLengths(lam1Rows);
    const timed2 = withLengths(lam2Rows);

    const rows: Record<SeriesKey, TimedShift[]> = {
      D1: timed1.filter((t) => isDayShift(t.row)),
      N1: timed1.filter((t) => !isDayShift(t.row)),
      D2: timed2.filter((t) => isDayShift(t.row)),
      N2: timed2.filter((t) => !isDayShift(t.row)),
      total: [...timed1, ...timed2],
    };

    const build = (pick: (r: LamShiftRollsRow) => boolean) =>
      Object.fromEntries(
        (Object.keys(rows) as SeriesKey[]).map((k) => {
          const kept = rows[k].filter((t) => pick(t.row));
          return [
            k,
            {
              buckets: byWeek(kept.map((t) => t.row)),
              potential: potentialByWeek(kept),
            },
          ];
        }),
      ) as Record<SeriesKey, SeriesData>;

    return { all: build(() => true), complete: build((r) => !r.is_partial) };
  }, [lam1Rows, lam2Rows]);

  /**
   * The OEE row as a chart: one point per week per crew, off the same
   * complete-shift figures the table scores from, so the two cannot disagree.
   *
   * A crew with no shifts that week is null rather than zero, so its line
   * breaks instead of diving to the floor. Lamination 1's crews (D1, N1) only
   * have shifts from 2026-09-22, when its PLC was commissioned; earlier weeks
   * are null for them.
   */
  const oeeTrend = useMemo(() => {
    const oee = MEASURABLES.find((m) => m.key === "oee");
    if (!oee) return [];
    return weeks.map((w) => {
      const point: Record<string, number | string | null> = { label: w.label };
      for (const sr of SERIES) {
        const data = complete[sr.key];
        const bucket = data.buckets.get(w.start.getTime());
        point[sr.key] = bucket
          ? oee.value(bucket, data.potential.get(w.start.getTime()) ?? 0)
          : null;
      }
      return point;
    });
  }, [weeks, complete]);

  /**
   * The narrowest the grid goes. Both halves stretch past it to fill the card;
   * below it they scroll together rather than squeezing the columns.
   */
  const gridWidth = LABEL_W + weeks.length * WEEK_W;

  const hasTrend = oeeTrend.some((p) =>
    SERIES.some((sr) => typeof p[sr.key] === "number"),
  );

  /** Which bucket set a measurable reads from. */
  const bucketsFor = (m: Measurable) => (m.completeShiftsOnly ? complete : all);

  /** Step to another quarter, or pick one. Kept on screen when a load fails. */
  const picker = (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      <IconButton
        size="small"
        aria-label="Previous quarter"
        disabled={quarter.getTime() <= oldest.getTime()}
        onClick={() => setQuarter((q) => addQuarters(q, -1))}
      >
        <ChevronLeftIcon fontSize="small" />
      </IconButton>
      <Select
        size="small"
        value={quarter.getTime()}
        onChange={(e) => setQuarter(new Date(Number(e.target.value)))}
        inputProps={{ "aria-label": "Quarter" }}
        sx={{ minWidth: 150 }}
      >
        {options.map((q) => (
          <MenuItem key={q.getTime()} value={q.getTime()}>
            {quarterName(q)}
            {q.getTime() === current.getTime() ? " (current)" : ""}
          </MenuItem>
        ))}
      </Select>
      <IconButton
        size="small"
        aria-label="Next quarter"
        disabled={quarter.getTime() >= current.getTime()}
        onClick={() => setQuarter((q) => addQuarters(q, 1))}
      >
        <ChevronRightIcon fontSize="small" />
      </IconButton>
    </Stack>
  );

  if (failed && !loading) {
    return (
      <>
        {picker}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Could not load roll counts for {quarterName(quarter)}.
        </Typography>
      </>
    );
  }

  /** One week's figure for a measurable, from whichever series' buckets. */
  const valueCell = (
    m: Measurable,
    data: SeriesData,
    w: ScorecardWeek,
    emphasis: boolean,
  ) => {
    const bucket = data.buckets.get(w.start.getTime());
    const value = bucket
      ? m.value(bucket, data.potential.get(w.start.getTime()) ?? 0)
      : null;

    // A week the line never ran did not produce nothing — it did not happen.
    // A 0 would be compared with the sheet as a real week of no output.
    if (value === null) {
      return (
        <TableCell
          key={w.weekNum}
          align="center"
          sx={{ ...weekCell, color: "text.disabled" }}
          title="No shifts recorded"
        >
          —
        </TableCell>
      );
    }

    return (
      <TableCell
        key={w.weekNum}
        align="center"
        title={
          bucket?.is_partial
            ? "This week is still in progress"
            : `${bucket?.shifts} shifts`
        }
        sx={{
          ...weekCell,
          whiteSpace: "nowrap",
          fontWeight: emphasis ? 700 : 500,
          // The running week has not finished, so it is not yet comparable
          // with the ones beside it.
          fontStyle: bucket?.is_partial ? "italic" : undefined,
          opacity: bucket?.is_partial ? 0.7 : 1,
        }}
      >
        {m.format(value)}
      </TableCell>
    );
  };

  const empty = weeks.every((w) => !all.total.buckets.has(w.start.getTime()));

  return (
    <LoadingOverlay show={loading} minHeight={240}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 1 }}
      >
        {picker}
        <Button
          size="small"
          startIcon={<FileDownloadOutlinedIcon />}
          // Nothing to export before the first fetch lands, and a file of bare
          // headers looks like a broken download rather than an empty quarter.
          disabled={loading || empty}
          onClick={() =>
            downloadCsv(
              buildCsv(weeks, all, complete),
              `weekly-rolls-${quarterLabel(quarter)}-${quarter.getFullYear()}.csv`,
            )
          }
        >
          Export CSV
        </Button>
      </Stack>

      {/* Table and chart share one scroller and one inner width: scrolled apart
          in separate containers, the columns would no longer line up. */}
      <Box sx={{ overflowX: "auto" }}>
        <Box sx={{ width: "100%", minWidth: gridWidth }}>
          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{ borderRadius: 2, overflowX: "visible" }}
          >
            <Table size="small" sx={{ tableLayout: "fixed", width: "100%" }}>
              <TableHead>
                {/* Each header row names what it holds, in its own first cell. */}
                <TableRow>
                  <TableCell sx={{ ...stickyCell, fontWeight: 700 }}>
                    Qtr
                  </TableCell>
                  {weeks.map((w) => (
                    <TableCell
                      key={w.weekNum}
                      align="center"
                      sx={{ ...weekCell, fontWeight: 700 }}
                    >
                      {quarterLabel(w.start)}
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  <TableCell sx={{ ...stickyCell, fontWeight: 700 }}>
                    Week Num
                  </TableCell>
                  {weeks.map((w) => (
                    <TableCell key={w.weekNum} align="center" sx={weekCell}>
                      {w.weekNum}
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  <TableCell sx={{ ...stickyCell, fontWeight: 700 }}>
                    Date
                  </TableCell>
                  {weeks.map((w) => (
                    <TableCell
                      key={w.weekNum}
                      align="center"
                      sx={{
                        ...weekCell,
                        fontWeight: 700,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {w.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {MEASURABLES.map((m) => {
                  const expanded = !!open[m.key];
                  return (
                    <React.Fragment key={m.key}>
                      {/* The section row is the plant total, and the handle for the
                      per-line figures under it. Collapsed by default: the
                      totals are what the sheet is compared against, and the
                      split only matters once a number looks wrong. */}
                      <TableRow
                        hover
                        onClick={() => toggle(m.key)}
                        sx={{
                          cursor: "pointer",
                          "& > td": { bgcolor: "action.hover" },
                        }}
                      >
                        <TableCell
                          sx={{ ...stickyCell, bgcolor: "action.hover" }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <IconButton
                              size="small"
                              aria-label={m.label}
                              aria-expanded={expanded}
                              onClick={(e) => {
                                // The row handles it; this would toggle twice.
                                e.stopPropagation();
                                toggle(m.key);
                              }}
                            >
                              <ExpandMoreIcon
                                fontSize="small"
                                sx={{
                                  transition: "transform 150ms",
                                  transform: expanded
                                    ? "rotate(180deg)"
                                    : "rotate(-90deg)",
                                }}
                              />
                            </IconButton>
                            <Box component="span" sx={{ fontWeight: 700 }}>
                              {m.label}
                            </Box>
                          </Box>
                        </TableCell>
                        {weeks.map((w) =>
                          valueCell(m, bucketsFor(m).total, w, true),
                        )}
                      </TableRow>

                      {expanded &&
                        SERIES.map((sr) => (
                          <TableRow key={`${m.key}-${sr.key}`} hover>
                            <TableCell sx={{ ...stickyCell, pl: 6 }}>
                              {sr.label}
                            </TableCell>
                            {weeks.map((w) =>
                              valueCell(m, bucketsFor(m)[sr.key], w, false),
                            )}
                          </TableRow>
                        ))}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <Card variant="outlined" sx={{ borderRadius: 2, mt: 2 }}>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                OEE % by shift
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {hasTrend
                  ? "Each crew's week, against the same reference pace."
                  : `No completed shifts scored in ${quarterName(quarter)}.`}
              </Typography>

              <ResponsiveContainer width="100%" height={280}>
                {/*
              Aligned to the table above, column for column.

              The y axis takes exactly the label column's width and the margins
              are flush, so the plot starts and ends where the week columns do.
              The hidden bar is what forces recharts onto a band scale: a plain
              line chart uses a point scale that puts the first week hard on the
              axis and offsets every point by half a column. Same trick, and the
              same reason, as the quality mix chart — see AxisAlign.test.tsx.
            */}
                <ComposedChart
                  data={oeeTrend}
                  margin={{ top: 8, right: 0, bottom: 0, left: 0 }}
                >
                  <Bar dataKey="__axisSpacer" hide />
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="label"
                    height={34}
                    tickMargin={8}
                    interval={0}
                    tick={{ fontSize: 11 }}
                  />
                  <YAxis
                    width={LABEL_W}
                    tickMargin={8}
                    // Fixed 0-100: a crew's week is read against the whole scale,
                    // not against whatever else happens to be on screen.
                    domain={[0, 100]}
                    tickFormatter={(v: any) => `${Math.round(Number(v))}%`}
                  />
                  <RechartsTooltip
                    formatter={(v: any, name: any) => [
                      `${Number(v).toFixed(1)}%`,
                      name,
                    ]}
                    labelFormatter={(l: any) => `Week beginning ${l}`}
                  />
                  <Legend />
                  {SERIES.map((sr) => (
                    <Line
                      key={sr.key}
                      type="linear"
                      dataKey={sr.key}
                      name={sr.label}
                      stroke={SHIFT_COLOR[sr.key]}
                      strokeWidth={2}
                      dot={{ r: 2 }}
                      activeDot={{ r: 5 }}
                      // A week a crew did not work breaks the line rather than
                      // joining across it as though nothing happened.
                      connectNulls={false}
                      isAnimationActive={false}
                    />
                  ))}
                </ComposedChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Box>
      </Box>

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ mt: 1, display: "block" }}
      >
        Weeks begin Sunday. A dash is a week with no recorded shifts for that
        crew; an italic figure is the week still in progress. B % of Output is B
        ÷ (A + B + F). OEE is A-rolls against what a shift could produce at 48.4
        s per roll across its full twelve hours, so downtime counts against it;
        a shift still running is left unscored until it ends. Figures are the
        PLC's own counts.
      </Typography>
    </LoadingOverlay>
  );
};

export default WeeklyRollsReport;
